import { getUruguayLocalityItems } from '../../mocks/UruguayLocalities';

export const DGI_STATUS_HABILITADO = 'Certificado de vigencia anual habilitado';
export const DGI_STATUS_INHABILITADO = 'Certificado de vigencia anual inhabilitado';
export const DGI_STATUS_SUSPENDIDO = 'Certificado de vigencia anual suspendido';
export const DGI_STATUS_NO_DEFINIDO = 'No definido';

export const DGI_STATUS_OPTIONS = [
  DGI_STATUS_HABILITADO,
  DGI_STATUS_INHABILITADO,
  DGI_STATUS_SUSPENDIDO,
  DGI_STATUS_NO_DEFINIDO,
] as const;

export type DgiStatusOption = (typeof DGI_STATUS_OPTIONS)[number];

export const toTitleCase = (value: string): string =>
  value
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

export const resolveUruguayLocality = (
  raw: string | null | undefined,
  department: string | null | undefined,
): string => {
  if (!raw?.trim()) return '';
  const trimmed = raw.trim();
  if (department) {
    const match = getUruguayLocalityItems(department).find(
      (item) => item.label.localeCompare(trimmed, undefined, { sensitivity: 'base' }) === 0,
    );
    if (match) return match.label;
  }
  return toTitleCase(trimmed);
};

const findExactDgiStatus = (raw: string): DgiStatusOption | undefined =>
  DGI_STATUS_OPTIONS.find(
    (status) => status.localeCompare(raw.trim(), undefined, { sensitivity: 'base' }) === 0,
  );

export const normalizeDgiStatus = (
  raw: string | null | undefined,
): DgiStatusOption | '' => {
  if (!raw?.trim()) return '';

  const exact = findExactDgiStatus(raw);
  if (exact) return exact;

  const normalized = raw.trim().toUpperCase();

  if (normalized.includes('SUSPEND')) return DGI_STATUS_SUSPENDIDO;
  if (normalized.includes('INHABILIT') || normalized.includes('INACTIV')) {
    return DGI_STATUS_INHABILITADO;
  }
  if (
    normalized.includes('HABILIT') ||
    normalized.includes('VIGEN') ||
    normalized === 'ACTIVO'
  ) {
    return DGI_STATUS_HABILITADO;
  }
  if (normalized.includes('NO DEFIN')) return DGI_STATUS_NO_DEFINIDO;

  return DGI_STATUS_NO_DEFINIDO;
};

export const defaultUruguayYesNo = (value: boolean | null | undefined): boolean =>
  value === true;

type UruguayBasicDataFields = {
  city?: string | null;
  legal_city?: string | null;
  state?: string | null;
  legal_state?: string | null;
  dgi_status?: string | null;
  contractual_link?: boolean | null;
  politically_exposed?: boolean | null;
  state_supplier_registry?: boolean | null;
};

export const normalizeUruguayBasicData = <T extends UruguayBasicDataFields>(basicData: T): T => ({
  ...basicData,
  city: resolveUruguayLocality(basicData.city, basicData.state),
  legal_city: resolveUruguayLocality(basicData.legal_city, basicData.legal_state),
  dgi_status: normalizeDgiStatus(basicData.dgi_status),
  contractual_link: defaultUruguayYesNo(basicData.contractual_link),
  politically_exposed: defaultUruguayYesNo(basicData.politically_exposed),
  state_supplier_registry: defaultUruguayYesNo(basicData.state_supplier_registry),
});
